// chatbotApi.js
import axios from 'axios';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8001';

const chatbotApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
chatbotApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('Chatbot API - Token found:', !!token);
    if (token) {
      config.headers.Authorization = `Token ${token}`;
      console.log('Chatbot API - Authorization header set:', config.headers.Authorization.substring(0, 20) + '...');
    }
    console.log('Chatbot API - Request URL:', config.baseURL + config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
chatbotApi.interceptors.response.use(
  (response) => {
    console.log('Chatbot API - Success response:', response.status);
    return response;
  },
  (error) => {
    console.error('Chatbot API - Error response:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

export const chatbotService = {
  // Send chat query with proper session handling
  async sendMessage(message, sessionId = null) {
    try {
      console.log('📤 Chatbot API - Sending message:', {
        message: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
        sessionId: sessionId || 'null (new conversation)',
        messageLength: message.length
      });
      
      // Prepare payload according to backend expectations
      const payload = {
        message: message.trim()
      };
      
      // Only include session_id if it's not null/undefined
      // Backend guide: send session_id: null or omit it for new conversations
      if (sessionId !== null && sessionId !== undefined) {
        payload.session_id = sessionId;
      }
      
      const response = await chatbotApi.post('/api/v1/support/chatbot/query/', payload);
      
      console.log('📥 Chatbot API - Response received:', {
        session_id: response.data.session_id,
        bot_response_length: response.data.bot_response?.content?.length || 0,
        context_used: response.data.context_used,
        status: 'success'
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Chatbot API - Error sending message:', {
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data
      });
      throw error;
    }
  },

  // Check chatbot health
  async checkHealth() {
    try {
      console.log('Checking chatbot health...');
      const response = await chatbotApi.get('/api/v1/support/chatbot/health/');
      console.log('Health check response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  },

  // Get user's conversations
  async getConversations() {
    try {
      const response = await chatbotApi.get('/api/v1/support/chat-conversations/');
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  // Get specific conversation
  async getConversation(id) {
    try {
      const response = await chatbotApi.get(`/api/v1/support/chat-conversations/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }
  },

  // Knowledge base methods
  async getKnowledgeBase() {
    try {
      console.log('Fetching knowledge base documents...');
      const response = await chatbotApi.get('/api/v1/support/knowledge-base/');
      console.log('Knowledge base response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      throw error;
    }
  },

  async uploadDocument(formData) {
    try {
      console.log('Uploading document to knowledge base...');
      const response = await chatbotApi.post('/api/v1/support/knowledge-base/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  async rebuildIndex() {
    try {
      console.log('Rebuilding search index...');
      const response = await chatbotApi.post('/api/v1/support/knowledge-base/rebuild_index/');
      console.log('Rebuild index response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error rebuilding index:', error);
      throw error;
    }
  },

  async getIndexStats() {
    try {
      console.log('Fetching index statistics...');
      const response = await chatbotApi.get('/api/v1/support/knowledge-base/index_stats/');
      console.log('Index stats response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting index stats:', error);
      throw error;
    }
  }
};

export default chatbotApi;
